import React, { useEffect, useState } from "react";
import { View, StyleSheet } from "react-native";

import {
  Text,
  Input,
  Toast,
  Button,
  Loading,
  CheckBox,
  EditModal,
  AttrPicker,
  InputLabel,
  ConfirmAlert,
  GroupInfoBox,
  AddItemButton,
  DeleteAlertComponent,
} from "components";

import { useFlag } from "hooks";
import { changeArrayAt, isEqualArrays, mq } from "utils";
import { colors, sizes, strings } from "assets/constants";

import { useAttribute } from "clients/apis";
import { authAtom } from "stores";
import { useRecoilValue } from "recoil";
import CatAttrBox from "./CatAttrBox";
import AlertDashedBox from "./AlertDashedBox";
import css from "styles";

export default function Attrs(props: {
  attrs: CategoryAttributeT[];
  onChange: (attrs: Array<any>) => void;
}) {
  const {
    company: { color },
  } = useRecoilValue(authAtom);

  const { attrs, onChange } = props;

  const [attrsState, setAttrsState] = useState<
    Array<CategoryAttributeT> | undefined
  >(attrs);
  useEffect(() => {
    setAttrsState(attrs);
  }, [attrs]);

  const [indexToEdit, setIndexToEdit] = useState<number | null>(null);
  const { flg: modal, on: showModal, off: hideModal } = useFlag();
  const { flg: alert, on: showAlert, off: hideAlert } = useFlag();

  const [fields, setFields] = useState<CategoryAttributeT>();
  const updateFields = (newVal: CategoryAttributeT) =>
    setFields((prev) => ({ ...prev, ...newVal }));

  const resetForm = () => {
    setIndexToEdit(null);
    setFields({});
  };

  const _onChangeAttr = (attr?: PickerItemT) => {
    updateFields({
      id: Number(attr?.id),
      title: attr?.title,
      items: undefined,
    });
  };
  const _onChangeItems = (items: AttributeItemT[]) => updateFields({ items });
  const _onChangeIsVariable = (state?: 1 | 0) => {
    updateFields({ is_variable: state, price_ratio: "" });
  };
  const _onChangePriceRatio = (price_ratio: string) =>
    updateFields({ price_ratio });

  const _onAdd = () => {
    fields && onChange(attrs ? [...attrs, fields] : [fields]);
    resetForm();
  };

  const _onEdit = () => {
    const tmp =
      typeof indexToEdit === "number" &&
      attrsState &&
      changeArrayAt(attrsState, indexToEdit, fields);
    tmp && onChange(tmp);
  };

  const _onDelete = (close: () => void) => {
    const tmp =
      typeof indexToEdit === "number" &&
      attrsState &&
      changeArrayAt(attrsState, indexToEdit);
    tmp && onChange(tmp);
    resetForm();
    close();
  };

  const _onConfirm = () => {
    if (!fields?.title || !fields.items || fields.items.length === 0) {
      Toast.error({ message: "ویژگی و مقادیر آن را انتخاب کنید" });
      return;
    }

    if (fields.is_variable && fields.price_ratio === "") {
      Toast.error({ message: " ضریب قیمت را وارد کنید" });
      return;
    }

    const existsAttrs = attrsState?.map((a) => a.items?.map((i) => i.id)) || [];
    const attrIndex = existsAttrs.findIndex((e) =>
      isEqualArrays(fields?.items?.map((i) => i.id) || [], e || [])
    );

    // if (attrIndex > -1) {
    //   if (indexToEdit === null || indexToEdit !== attrIndex) {
    //     Toast.warning({
    //       message: "این ویژگی از قبل وجود دارد",
    //     });
    //     return;
    //   }

    //   if (
    //     Number(fields.is_variable) ===
    //       Number(attrsState[indexToEdit].is_variable) &&
    //     Number(fields.price_ratio) ===
    //       Number(attrsState[indexToEdit].price_ratio)
    //   ) {
    //     Toast.warning({
    //       message: "این ویژگی از قبل وجود دارد",
    //     });
    //     return;
    //   }
    // }

    indexToEdit === null ? _onAdd() : _onEdit();
    resetForm();
    hideModal();
  };

  const _onCancel = () => {
    resetForm();
    hideModal();
  };

  const {
    data: attr,
    isFetching: attrLoading,
    refetch: refetchAttr,
  } = useAttribute(fields?.id);
  const attrItems = attr?.data.items;
  useEffect(() => {
    fields?.id && refetchAttr();
  }, [fields?.id, refetchAttr]);

  return (
    <>
      <View style={css.responsiveListContainer}>
        {attrsState && attrsState?.length > 0 ? (
          attrsState?.map((attribute: CategoryAttributeT, key: number) => {
            return (
              <View key={String(key)} style={css.responsiveBoxContainer}>
                <GroupInfoBox
                  key={String(key)}
                  onEdit={() => {
                    setIndexToEdit(key);
                    setFields(attribute);

                    showModal();
                  }}
                  onDelete={() => {
                    setIndexToEdit(key);
                    showAlert();
                  }}
                  title={attribute.title}
                  icon={{ name: "chevrons-left" }}
                  renderContent={() => <CatAttrBox attr={attribute} />}
                />
              </View>
            );
          })
        ) : (
          <Text
            size="sm"
            color={colors.textColorGray}
            style={{ marginBottom: sizes.space / 2 }}
          >
            {strings.no_attr_cat}
          </Text>
        )}
      </View>

      <AddItemButton onPress={showModal} />

      <EditModal
        visible={modal}
        onCancel={_onCancel}
        onConfirm={_onConfirm}
        renderForm={() => {
          return (
            <>
              <InputLabel label={strings.attribute} />
              <AttrPicker
                value={{ title: fields?.title }}
                onSelect={_onChangeAttr}
              />

              <View style={{ marginBottom: mq(20) }}>
                <>
                  <InputLabel label={strings.items} />
                  {!fields?.id ? (
                    <AlertDashedBox text={strings.pick_attr_first} />
                  ) : attrItems?.length === 0 ? (
                    <AlertDashedBox text={strings.empty_attr_items} />
                  ) : (
                    <Text
                      size={"sm"}
                      style={{ marginRight: sizes.space / 2, top: mq(-5) }}
                    >
                      {strings.pick_attr_items}
                    </Text>
                  )}
                </>

                <View style={styles.attrItemsSection}>
                  {attrLoading ? (
                    <Loading style={{ top: mq(10) }} size={15} />
                  ) : (
                    attrItems?.map((item: AttributeItemT, key: number) => {
                      const itemIndex = Number(
                        fields?.items?.findIndex((e) => e.id === item.id)
                      );
                      const isSelected = itemIndex > -1;
                      return (
                        <Button
                          key={String(key)}
                          onPress={() => {
                            let tmp = fields?.items ?? [];
                            if (isSelected) {
                              tmp = changeArrayAt(tmp, itemIndex);
                            } else {
                              tmp?.push(item);
                            }
                            _onChangeItems(tmp);
                          }}
                          width={"auto"}
                          height={mq(24)}
                          title={{ value: item.value, size: "sm" }}
                          contentSeparator={4}
                          bgColor={isSelected ? color : colors.white}
                          style={{
                            paddingHorizontal: mq(15),
                            marginLeft: mq(7),
                            opacity: isSelected ? 1 : 0.8,
                            borderWidth: 1,
                            borderColor: colors.textColorGray,
                            marginTop: sizes.space,
                          }}
                        />
                      );
                    })
                  )}
                </View>
              </View>

              <>
                <CheckBox
                  title={strings.effective_in_variation}
                  value={fields?.is_variable || 0}
                  onChange={_onChangeIsVariable}
                />
                <Text style={{ lineHeight: 20 }} color={colors.textColorGray}>
                  {strings.effective_in_variation_description}
                </Text>
                <View style={{ height: sizes.space }} />

                {Number(fields?.is_variable) > 0 && (
                  <>
                    <InputLabel label={strings.price_ratio} />
                    <Text
                      style={{ top: mq(-7), lineHeight: 20 }}
                      color={colors.textColorGray}
                    >
                      {strings.price_ratio_description}
                    </Text>
                    <Input
                      onChangeText={_onChangePriceRatio}
                      value={fields?.price_ratio + ""}
                      icon={{ name: "bar-chart-2" }}
                      placeholder={strings.insert_price_ratio}
                      keyboardType={"number-pad"}
                    />
                  </>
                )}
              </>
            </>
          );
        }}
      />

      <ConfirmAlert
        force
        show={alert}
        hide={hideAlert}
        renderContent={(close) => (
          <DeleteAlertComponent
            description={strings.delete_category_attr_description}
            removeLoading={false}
            remove={() => _onDelete(close)}
            cancel={close}
          />
        )}
      />
    </>
  );
}

const styles = StyleSheet.create({
  attrItemsSection: {
    flexWrap: "wrap",
    alignItems: "center",
    flexDirection: "row-reverse",
    marginTop: mq(5),
  },

  tag: {
    paddingHorizontal: mq(10),
    borderRadius: 20,
    borderWidth: 1,
    borderColor: colors.border,
    flexDirection: "row-reverse",
  },
});
